"use client";

import { useCallback, useRef, useState } from "react";
import { ArrowRightIcon } from "@phosphor-icons/react/ssr";

import Container from "~/componentsDS/Layout/Container";
import Heading from "~/componentsDS/Typography/Heading";
import Carousel from "~/componentsDS/Carousel";
import Reveal from "~/componentsDS/Reveal";
import {
    type CarouselApi,
    CarouselContent,
    CarouselItem,
} from "~/components/ui/carousel";
import usePrevNextButtons from "~/componentsND/carousel/usePrevNextButtons";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { cn } from "~/lib/utils";
import { SYMPTOM_SLIDES } from "./data";
import { TreatmentCard } from "./TreatmentCard";
import { ExpertQuoteCard } from "./ExpertQuoteCard";

const PREV_NEXT_BUTTONS_CLASSNAMES = "inline-flex size-10 items-center justify-center rounded-full text-pureWhite transition-colors hover:bg-pureWhite hover:text-primary400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

export default function SupportForSymptomsSection() {
    const sendEvent = useTrackEvent();
    const [carouselApi, setCarouselApi] = useState<CarouselApi>();
    const [activeIndex, setActiveIndex] = useState(0);
    const tabListRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const pendingClickTextRef = useRef<string | null>(null);
    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick,
    } = usePrevNextButtons(carouselApi);

    const trackInteraction = useCallback((clickText: string) => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "hormonal_stage",
                meta_data: {
                    click_text: clickText,
                    product_name: "oestra",
                },
            },
        });
    }, [sendEvent]);

    const handleSelect = useCallback((api: CarouselApi) => {
        if (!api) {
            return;
        }

        const index = api.selectedScrollSnap();
        const slide = SYMPTOM_SLIDES[index];
        const clickText = pendingClickTextRef.current ?? slide?.category;
        pendingClickTextRef.current = null;

        if (clickText) {
            trackInteraction(clickText);
        }

        setActiveIndex(index);

        const tabList = tabListRef.current;
        const activeTab = tabRefs.current[index];

        if (tabList && activeTab) {
            const tabListRect = tabList.getBoundingClientRect();
            const activeTabRect = activeTab.getBoundingClientRect();

            tabList.scrollTo({
                behavior: "smooth",
                left: tabList.scrollLeft
                    + activeTabRect.left
                    - tabListRect.left
                    - (tabList.clientWidth - activeTabRect.width) / 2,
            });
        }
    }, [trackInteraction]);

    const handleTabClick = useCallback((index: number, category: string) => {
        if (!carouselApi || carouselApi.selectedScrollSnap() === index) {
            trackInteraction(category);
            return;
        }

        pendingClickTextRef.current = category;
        carouselApi.scrollTo(index);
    }, [carouselApi, trackInteraction]);

    const handlePreviousClick = useCallback(() => {
        if (!carouselApi?.canScrollPrev()) {
            return;
        }

        const previousSlide = SYMPTOM_SLIDES[carouselApi.selectedScrollSnap() - 1];
        pendingClickTextRef.current = previousSlide?.category ?? null;
        onPrevButtonClick();
    }, [carouselApi, onPrevButtonClick]);

    const handleNextClick = useCallback(() => {
        if (!carouselApi?.canScrollNext()) {
            return;
        }

        const nextSlide = SYMPTOM_SLIDES[carouselApi.selectedScrollSnap() + 1];
        pendingClickTextRef.current = nextSlide?.category ?? null;
        onNextButtonClick();
    }, [carouselApi, onNextButtonClick]);

    return (
        <section className="overflow-hidden pt-16 md:pt-0 md:pb-16 lg:pb-24">
            <Container>
                <Reveal>
                    <Heading as="h3" size="sm" sizes={{ lg: "md" }} className="text-pureWhite lg:text-center">
                        Support for symptoms <br className="lg:hidden"/>
                        at <Heading as="i" font="featureDisplay" weight="400" className="text-[28px] lg:text-[36px]">every</Heading> hormonal stage
                    </Heading>
                </Reveal>

                <div className="mx-auto mt-6 max-w-[1152px] lg:mt-8">
                    <div className="flex items-center lg:justify-between">
                        <div
                            ref={tabListRef}
                            role="tablist"
                            aria-label="Symptom categories"
                            className="-mx-6 flex gap-2 overflow-x-auto px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:overflow-visible lg:px-0"
                        >
                            {SYMPTOM_SLIDES.map(({ category }, index) => {
                                const isActive = index === activeIndex;

                                return (
                                    <button
                                        key={category}
                                        id={`symptoms-tab-${index}`}
                                        ref={(element) => {
                                            tabRefs.current[index] = element;
                                        }}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        aria-controls={`symptoms-slide-${index}`}
                                        onClick={() => handleTabClick(index, category)}
                                        className={cn(
                                            "flex h-10 shrink-0 items-center rounded-full px-4 transition-colors",
                                            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-white",
                                            isActive
                                                ? "bg-[#19223D]"
                                                : "bg-pureWhite/10 [@media(hover:hover)]:hover:bg-pureWhite/25",
                                        )}
                                    >
                                        <span className="inline-block pt-[3px] font-suisseBP text-[14px] font-medium uppercase leading-[20px] tracking-[1.12px] text-pureWhite">{category}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="hidden shrink-0 items-center gap-1 pl-6 lg:flex">
                            <button
                                type="button"
                                disabled={prevBtnDisabled}
                                onClick={handlePreviousClick}
                                aria-label="Previous symptom"
                                className={cn(
                                    PREV_NEXT_BUTTONS_CLASSNAMES,
                                    prevBtnDisabled && "pointer-events-none opacity-50"
                                )}
                            >
                                <ArrowRightIcon size={24} className="rotate-180" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                disabled={nextBtnDisabled}
                                onClick={handleNextClick}
                                aria-label="Next symptom"
                                className={cn(
                                    PREV_NEXT_BUTTONS_CLASSNAMES,
                                    nextBtnDisabled && "pointer-events-none opacity-50"
                                )}
                            >
                                <ArrowRightIcon size={24} aria-hidden="true" />
                            </button>
                        </div>
                    </div>

                    <Carousel
                        setApi={setCarouselApi}
                        onSelect={handleSelect}
                        arrows={false}
                        loop={false}
                        dots={false}
                        desktopWheelGestures
                        opts={{
                            align: "center",
                            containScroll: "trimSnaps",
                            skipSnaps: false,
                        }}
                        className="-mx-6 mt-4 lg:mx-0"
                    >
                        <CarouselContent className="ml-0 gap-2 px-6 lg:gap-0 lg:px-0">
                            {SYMPTOM_SLIDES.map((slide, index) => (
                                <CarouselItem
                                    id={`symptoms-slide-${index}`}
                                    key={slide.category}
                                    aria-labelledby={`symptoms-tab-${index}`}
                                    role="tabpanel"
                                    className="pl-0 basis-full last:mr-6 lg:last:mr-0"
                                >
                                    <div className="grid gap-2 max-lg:h-full max-lg:grid-rows-[auto_1fr] lg:grid-cols-[minmax(0,2.86fr)_minmax(240px,1fr)] lg:gap-4">
                                        <TreatmentCard slide={slide} />
                                        <ExpertQuoteCard
                                            slide={slide}
                                            isActive={index === activeIndex}
                                            keepAuthorAlt={index === 0}
                                        />
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>
                </div>
            </Container>
        </section>
    );
}
